import type { IndividualPaymentPaymentSerialViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentParcelsParcelidSerials } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Janela em que os boletos já carregados são considerados frescos.
 *
 * Mesmo motivo de `useIndividualPaymentContracts`: o `queryClient` do app usa
 * `staleTime: 0`, o que faria cada expandir/recolher remontar o observer e
 * disparar uma requisição nova. Dentro desta janela, reabrir a mesma parcela é
 * instantâneo e não gera tráfego.
 */
const SERIALS_STALE_TIME = 30_000;

/**
 * Boletos de uma parcela, carregados sob demanda ao expandir a linha.
 *
 * Não existe endpoint em lote para boletos: cada parcela expandida faz a sua
 * própria requisição. Por isso este hook é instanciado por linha — o estado de
 * carregamento e o de erro ficam locais, e uma parcela com falha não afeta as
 * outras nem bloqueia a tabela.
 *
 * @param parcelId - ID da parcela.
 * @param isOpen - Se a linha da parcela está expandida.
 *
 * @example
 * ```tsx
 * const { serials, isLoading, isError } = useParcelSerials(parcelId, isOpen);
 * ```
 */
export function useParcelSerials(parcelId: number, isOpen: boolean) {
  const { data, isLoading, isFetching, isError } =
    useGetApiV1IndividualpaymentParcelsParcelidSerials(parcelId, {
      query: { enabled: isOpen, staleTime: SERIALS_STALE_TIME },
    });

  const serials: readonly IndividualPaymentPaymentSerialViewModel[] =
    unwrapPagedResponse<IndividualPaymentPaymentSerialViewModel>(data);

  return {
    serials,
    isLoading: isOpen && isLoading,
    /** Refetch em andamento sobre dados já exibidos — usado para esmaecer a seção. */
    isFetching: isOpen && isFetching && !isLoading,
    isError,
  } as const;
}
